import { CheckOutlined, TeamOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { AccordionAPI } from '@mm/components/src/AccordionItem/AccordionItem';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { KlaviyoIcon } from '@mm/icons';
import { useKlaviyoCRUDActions } from '@mm/state/actions/KlaviyoCRUDActions';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import ConnectionModel, {
  KlaviyoAccessToken,
  KlaviyoOauthToken,
} from '@mm/state/model/ConnectionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { KlaviyoErrorMessage } from './KlaviyoErrorMessage';
import styles from './KlaviyoIntegrations.module.css';
import { KlaviyoSettings } from './KlaviyoSettings';
import { useConnectKlaviyo } from './use-connect-klaviyo';

export type KlaviyoSetupMode = 'oauth' | 'api_key' | null;

const connectionSetupHeader = (onClick: () => void) => (
  <Button
    css={{ container: 'integrations__header-add-button-secondary' }}
    onClick={e => {
      e?.stopPropagation();
      onClick();
    }}
    color={ButtonColor.secondaryBlue}
  >
    Setup Your Connection
  </Button>
);

const syncWarning = (showSyncWarning: boolean) => {
  if (showSyncWarning) {
    return (
      <KlaviyoErrorMessage
        classNames={styles.syncWarning}
        error="Not Syncing"
        iconSize="16px"
      />
    );
  }
  return null;
};

const leadsSentCounter = (crmsLeadsSent?: number) => {
  if (crmsLeadsSent) {
    return (
      <div className="integrations__header-status">
        <TeamOutlined className="integrations__header-status-icon" />
        <div className="integrations__header-status-count">{crmsLeadsSent}</div>
        Leads sent
      </div>
    );
  }
  return null;
};

const apiKeyHeader = (showSyncWarning: boolean, crmsLeadsSent?: number) => (
  <div className="integrations__header-actions">
    {syncWarning(showSyncWarning)}
    {leadsSentCounter(crmsLeadsSent)}
    <Button
      css={{ container: 'integrations__header-add-button-secondary' }}
      onClick={e => {
        e?.stopPropagation();
        window.open(
          'https://www.klaviyo.com/settings/account/api-keys',
          '_blank',
        );
      }}
      color={ButtonColor.secondaryBlue}
    >
      Add a Connection
    </Button>
  </div>
);

const modeHeader = (
  mode: KlaviyoSetupMode,
  setMode: (mode: KlaviyoSetupMode) => void,
  connection: ConnectionModel | undefined,
  apiKeys: KlaviyoAccessToken[],
  oauthTokens: KlaviyoOauthToken[],
  showSyncWarning: boolean,
  crmsLeadsSent?: number,
) => {
  const action = mode === 'api_key' ? 'oauth' : 'api_key';
  const matchingApiKey = apiKeys.find(
    apiKey => apiKey.id === connection?.klaviyoAccessToken?.id,
  );
  const matchingOauthToken = oauthTokens.find(
    token => token.id === connection?.klaviyoOauthToken?.id,
  );
  const selectedToken = matchingApiKey || matchingOauthToken;
  const readOnly = selectedToken?.readOnly;
  let connectButton: string | null = null;
  let configuredButton: string | null = null;
  const connectionPatterns = {
    apiKey: {
      isConnected: !!matchingApiKey,
      configuredButton: 'Connected with API Key',
      shouldConnectDirectly:
        !matchingApiKey || matchingApiKey?.status === 'error',
      connectButton: 'Connect Directly',
    },
    oauth: {
      isConnected: !!matchingOauthToken,
      configuredButton: 'Connected Directly',
      shouldConnectWithApiKey:
        !matchingOauthToken || !!matchingOauthToken?.errorInfo,
      connectButton: 'Connect with API Key',
    },
  };

  const apiConfiguredPattern = connectionPatterns.apiKey;
  const oauthConfiguredPattern = connectionPatterns.oauth;

  if (mode === 'api_key') {
    if (apiConfiguredPattern.isConnected) {
      configuredButton = apiConfiguredPattern.configuredButton;
    }

    if (apiConfiguredPattern.shouldConnectDirectly) {
      connectButton = apiConfiguredPattern.connectButton;
    }
  }

  if (mode === 'oauth') {
    if (oauthConfiguredPattern.isConnected) {
      configuredButton = oauthConfiguredPattern.configuredButton;
    }

    if (oauthConfiguredPattern.shouldConnectWithApiKey) {
      connectButton = oauthConfiguredPattern.connectButton;
    }
  }
  return (
    <div className="integrations__header-actions">
      {syncWarning(showSyncWarning)}
      {leadsSentCounter(crmsLeadsSent)}
      {connectButton ? (
        <Button
          css={{
            container: classNames('integrations__header-add-button-secondary', {
              'integrations__header-add-button-secondary-static-disabled':
                !!readOnly,
            }),
          }}
          type="button"
          disabled={!!readOnly}
          onClick={e => {
            e?.stopPropagation();
            if (readOnly) {
              return;
            }
            setMode(action);
          }}
          color={ButtonColor.secondaryBlue}
        >
          {connectButton}
        </Button>
      ) : null}
      {configuredButton ? (
        <Button
          css={{
            container: classNames(
              'integrations__header-add-button-secondary',
              'integrations__header-add-button-secondary-static-disabled',
            ),
          }}
          type="button"
          disabled
          leftIcon={<CheckOutlined />}
          color={ButtonColor.secondaryBlue}
        >
          {configuredButton}
        </Button>
      ) : null}
    </div>
  );
};

export const KlaviyoIntegrations = () => {
  const features = useSelector(combinedFeatureFlagsSelector);
  const hasNativeIntegrationsFeature = features.nativeIntegrations;
  const {
    accounts,
    apiKeys,
    klaviyoConnections,
    getKlaviyoAccounts,
    getKlaviyoApiKeys,
    connectKlaviyoToken,
    getKlaviyoConnections,
    connectionsMessage,
  } = useConnectKlaviyo();
  const { invalidateStore: invalidateKlaviyoStore } = useKlaviyoCRUDActions();
  const { invalidateStore: invalidateSuppressionsStore } =
    useSuppressionListsCRUDActions();
  const { openNewBillingModal2023 } = useModalActions();
  const [accountsFetched, setAccountsFetched] = useState(false);
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.KLAVIYO_UPGRADE,
      onSuccessBilling,
    });
  };

  const sectionProps = {
    landmineName: landmineNames.KLAVIYO_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };

  const [mode, setMode] = useState<KlaviyoSetupMode>(null);
  const klaviyoConnection = klaviyoConnections?.[0];
  const allowModeSelector = !!klaviyoConnection?.klaviyoOauthToken;
  const token =
    klaviyoConnection?.klaviyoAccessToken ||
    klaviyoConnection?.klaviyoOauthToken;
  const showSyncWarning = token
    ? !!(
        connectionsMessage ||
        token.errorInfo ||
        ((token as KlaviyoOauthToken).missingScopes || []).length
      )
    : false;

  useEffect(() => {
    if (mode === null) {
      if (klaviyoConnection?.klaviyoOauthToken) {
        setMode('oauth');
      } else if (klaviyoConnection?.klaviyoAccessToken) {
        setMode('api_key');
      }
    }
  }, [klaviyoConnection, mode]);

  // loads webhooks once oauth tokens/api keys are loaded
  useEffect(() => {
    if (accountsFetched) {
      getKlaviyoConnections();
    }
  }, [accountsFetched]);

  useEffect(() => {
    (async () => {
      await getKlaviyoAccounts();
      await getKlaviyoApiKeys();
      setAccountsFetched(true);
    })();
    return () => {
      invalidateKlaviyoStore();
      invalidateSuppressionsStore();
    };
  }, []);

  const ref = useRef<AccordionAPI>(null);

  let header: JSX.Element;

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent = crmsLeadsSent?.klaviyoContactsSent;

  if (allowModeSelector) {
    header = modeHeader(
      mode,
      (mode: KlaviyoSetupMode) => {
        ref.current?.open();
        setMode(mode);
      },
      klaviyoConnection,
      apiKeys,
      accounts,
      showSyncWarning,
      leadsSent,
    );
  } else {
    header = apiKeyHeader(showSyncWarning, leadsSent);
  }

  return (
    <BaseIntegration
      accordionRef={ref}
      titleComponent={
        <BaseIntegrationHeader
          title="Klaviyo"
          upgradeButtonName="Set up your connection"
          subtitle={`Automatically sync your ${companyName} contacts to Klaviyo`}
          icon={<KlaviyoIcon />}
          headerRightComponent={header}
          css={{ icon: 'integrations__klaviyo-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="klaviyo"
    >
      <KlaviyoSettings
        connectionsMessage={connectionsMessage}
        klaviyoConnections={klaviyoConnections}
        onSelectMode={mode => setMode(mode)}
        connectToken={connectKlaviyoToken}
        mode={allowModeSelector ? mode : 'api_key'}
        showUpgradeModal={showUpgradeModal}
        hasNativeIntegrationsFeature={hasNativeIntegrationsFeature}
      />
    </BaseIntegration>
  );
};
